import { useState, useEffect } from 'react';
import { NewsCategory } from '../types';
import { IconPlus, IconFolder, IconPencil, IconTrash, IconRefresh } from '@tabler/icons-react';
import {
  listCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from '../services/categoriesService';
import { reassignCategories } from '../services/newsService';
import CategoryModal from '../components/CategoryModal';

export default function NewsCategoriesPage() {
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<NewsCategory | null>(null);
  const [reassigning, setReassigning] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const cats = await listCategories();
      setCategories(cats);
    } catch (error) {
      console.error('Error loading categories:', error);
      alert('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCategory(null);
    setShowModal(true);
  };

  const handleEdit = (category: NewsCategory) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleDelete = async (category: NewsCategory) => {
    if (!confirm(`Are you sure you want to delete "${category.id}"?`)) {
      return;
    }

    try {
      await deleteCategory(category.id);
      await loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    }
  };

  const handleSave = async (categoryData: Omit<NewsCategory, 'id'> & { id?: string }) => {
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, categoryData);
      } else {
        await createCategory(categoryData);
      }
      setShowModal(false);
      await loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      throw error;
    }
  };

  const handleReassignCategories = async () => {
    if (
      !confirm(
        'This will reassign categories to all articles based on current category keywords. This may take a while. Continue?'
      )
    ) {
      return;
    }

    try {
      setReassigning(true);
      const result = await reassignCategories();
      alert(`Success! ${result.updated} out of ${result.processed} articles were updated.`);
    } catch (error) {
      console.error('Error reassigning categories:', error);
      alert('Failed to reassign categories. Check console for details.');
    } finally {
      setReassigning(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div className="page-header">
      <div className="row g-2 align-items-center">
        <div className="col">
          <div className="page-pretitle">News</div>
          <h2 className="page-title">Categories</h2>
        </div>
        <div className="col-auto">
          <button
            onClick={handleReassignCategories}
            className="btn btn-outline-primary me-2"
            disabled={reassigning}
          >
            <IconRefresh className="icon icon-sm me-1" />
            {reassigning ? 'Reassigning...' : 'Reassign to Articles'}
          </button>
          <button onClick={handleAdd} className="btn btn-primary">
            <IconPlus className="icon" />
            Add Category
          </button>
        </div>
      </div>

      {/* Categories Table */}
      <div className="card mt-3">
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Keywords</th>
                <th>Status</th>
                <th className="w-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-5">
                    <div className="empty">
                      <div className="empty-icon">
                        <IconFolder size={48} stroke={1.5} />
                      </div>
                      <p className="empty-title">No categories found</p>
                      <p className="empty-subtitle text-muted">
                        Get started by creating your first category
                      </p>
                      <div className="empty-action">
                        <button onClick={handleAdd} className="btn btn-primary">
                          <IconPlus className="icon" />
                          Add your first category
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                categories.map(category => (
                  <tr key={category.id}>
                    <td>
                      <div>
                        <div className="font-weight-medium">{category.name}</div>
                      </div>
                    </td>
                    <td>
                      <div className="text-muted small">
                        {category.keywords && category.keywords.length > 0
                          ? category.keywords.slice(0, 3).join(', ') +
                            (category.keywords.length > 3 ? '...' : '')
                          : '-'}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`badge text-white ${category.isActive !== false ? 'bg-success' : 'bg-secondary'}`}
                      >
                        {category.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="btn-list flex-nowrap">
                        <button
                          onClick={() => handleEdit(category)}
                          className="btn btn-icon"
                          title="Edit"
                        >
                          <IconPencil className="icon" />
                        </button>
                        <button
                          onClick={() => handleDelete(category)}
                          className="btn btn-icon text-danger"
                          title="Delete"
                        >
                          <IconTrash className="icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <CategoryModal
          category={editingCategory}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
